import type { UseAsyncStatus } from '../useAsyncStatus';

export enum RequestState {
  Pending,
  Succeeded,
  Failed,
}

export enum Method {
  GET,
  PUT,
  POST,
  DELETE,
}

export type PendingRequestEvent<Context> = {
  state: RequestState.Pending;
  context: Context;
};

export type SucceededRequestEvent<Context> = {
  state: RequestState.Succeeded;
  context: Context;
};

export type FailedRequestEvent<Context> = {
  state: RequestState.Failed;
  context: Context;
  error: string;
};

export type GetRequestEventContext<Data, Context = Record<string, unknown>> = Context & {
  method: Method.GET;
  data?: Data;
};

export type PutRequestEventContext<Data, Context = Record<string, unknown>> = Context & {
  method: Method.PUT;
  data?: Data;
};

export type PostRequestEventContext<Data, Context = Record<string, unknown>> = Context & {
  method: Method.POST;
  data: Data;
};

export type DeleteRequestEventContext<Data = undefined, Context = Record<string, unknown>> = Context & {
  method: Method.DELETE;
  data?: Data;
};

/**
 * A RequestEvent reports a change in a request's state.
 */
export type RequestEvent<Context> =
  | PendingRequestEvent<Context>
  | SucceededRequestEvent<Context>
  | FailedRequestEvent<Context>;

/**
 * A RequestEventEmitter allows components to subscribe to events (state changes) in an API-synced data store hook such
 * as `useUserInvitations`.
 * @deprecated - This pattern is outdated -- use useAsyncValue and friends (like useAsyncRequest) instead.
 */
export type RequestEventEmitter<Context> = {
  subscribe(fn: (event: RequestEvent<Context>) => void): void;
  unSubscribe(fn: (event: RequestEvent<Context>) => void): void;
  emit(event: RequestEvent<Context>): void;
};

/**
 * @deprecated - This pattern is outdated -- use useAsyncValue and friends (like useAsyncRequest) instead.
 */
export type MakeRequestOptions<Data, EventContext> = {
  // A function that makes a network request and returns a promise holding the parsed response body.
  req: () => Promise<{ data?: Data }>;
  // A function that takes the data from a response to do whatever the caller needs to do with it.
  setData: (responseData: Data | undefined) => void;
  // A function that returns extra context details to pass in calling eventHandler.
  getEventContext: (data: Data | undefined, state: RequestState) => EventContext;
  // An object with functions that update the caller's state as the request progresses.
  statusFns: Pick<UseAsyncStatus, 'onPending' | 'onSuccess' | 'onError'>;
  // A RequestEventEmitter for reporting events as the request progresses.
  eventEmitter: RequestEventEmitter<EventContext>;
};

/**
 * Make a network request and respond to changes in the request's state. This function is designed to minimize the
 * amount of boilerplate code that needs to be written in a stateful hook that allows its data to be modified via API
 * requests, such as the `useUserInvitations` hook.
 *
 * This function takes both a `statusFns` object and an optional `eventEmitter` because they are meant for different
 * purposes: the status functions are held by the hook that calls makeRequest for updating the loading or error values
 * within the hook; on the other hand, the event emitter function is meant for components that use the hook to easily
 * plug in to request pending/success/error events.
 * @deprecated - This pattern is outdated -- use useAsyncValue and friends (like useAsyncRequest) instead.
 */
export const makeRequest = async <Data, EventContext>({
  req,
  setData,
  getEventContext,
  statusFns,
  eventEmitter,
}: MakeRequestOptions<Data, EventContext>) => {
  // Update the calling hook's status.
  statusFns.onPending();
  // Notify the component that uses the calling hook of the pending event.
  eventEmitter.emit({ state: RequestState.Pending, context: getEventContext(undefined, RequestState.Pending) });

  try {
    const response = await req();

    // Update the calling hook's data.
    setData(response.data);

    // Update the calling hook's status.
    statusFns.onSuccess();
    // Notify the component that uses the calling hook of the success event.
    eventEmitter.emit({
      state: RequestState.Succeeded,
      context: getEventContext(response.data, RequestState.Succeeded),
    });
  } catch (err) {
    // Update the calling hook's status.
    const errorMessage = statusFns.onError(err);
    // Notify the component that uses the calling hook of the failure event.
    eventEmitter.emit({
      state: RequestState.Failed,
      context: getEventContext(undefined, RequestState.Failed),
      error: errorMessage,
    });
  }
};
